import { apiFetch } from '@app/lib/api'
import type {
  StatusFilter,
  EmirDetailResponse,
  EmirListResponse,
  BoardResponse,
} from './production-api'

/**
 * Typed request helpers for the production module.
 *
 * Uses the shared `lib/api.ts` wrapper; response types come from the
 * `production-api.d.ts` contract shared with the Worker, so screens fail to
 * compile when the API changes.
 */

export interface EmirListArgs {
  status: StatusFilter
  query: string
  /**
   * Korgun `Proses_M.Pro` code ('01'..'08'); omitted = every process.
   *
   * Server side: /api/production applies an open-work EXISTS filter over
   * Urtx_wait_gch/Urtx_con_gch for this code (list, counts and export).
   */
  proses?: string
  page: number
  pageSize?: number
}

export const productionApi = {
  board: () => apiFetch<BoardResponse>('/production/board'),
  emirs: ({ status, query, proses, page, pageSize = 50 }: EmirListArgs) => {
    const params = new URLSearchParams({
      status,
      page: String(page),
      pageSize: String(pageSize),
    })
    if (query) params.set('query', query)
    // Set by the process board: "show me the emirs sitting in MONTE".
    if (proses) params.set('proses', proses)
    return apiFetch<EmirListResponse>(`/production?${params.toString()}`)
  },
  emir: (emirNo: number) => apiFetch<EmirDetailResponse>(`/production/${emirNo}`),
}

export const productionKeys = {
  board: ['production', 'board'] as const,
  emirs: (args: EmirListArgs) =>
    [
      'production',
      'emirs',
      args.status,
      args.query,
      args.proses ?? '',
      args.page,
      args.pageSize ?? 50,
    ] as const,
  emir: (emirNo: number) => ['production', 'emir', emirNo] as const,
}
